import type { CallSortBy } from '@relay-towing/api';
import type { CallSection, CallView } from './driverAppTabs';

const OPEN_STATUSES = ['NEW', 'PENDING', 'DISPATCHED', 'ON_SCENE', 'TOWING'];

/** A call the driver has started and not finished. */
export const UNDER_WAY = new Set(['DISPATCHED', 'ON_SCENE', 'TOWING']);

/**
 * The GET /calls query for each of the calls list's views. The server
 * scopes every one by the caller's permissions; `assignedToMe` asks for the
 * member's own calls, which is what Assigned is, not a permission filter.
 */
export function callListParams(view: CallView) {
  switch (view) {
    case 'active':
      return {
        status: OPEN_STATUSES,
        sortBy: 'board' satisfies CallSortBy,
      };
    case 'assigned':
      // With an outside call they took, waiting for the motor club to
      // confirm it, so it does not vanish from both lists once accepted.
      return {
        assignedToMe: true,
        status: [...OPEN_STATUSES, 'ACCEPTED_AWAITING'],
        sortBy: 'board' satisfies CallSortBy,
      };
    case 'completed':
      return {
        status: ['COMPLETED'],
        sortBy: 'date-desc' satisfies CallSortBy,
      };
  }
}

/**
 * The GET /calls query for a section above a view's calls (CALL_SECTIONS).
 * The server answers `outsideOffers` with the outside calls on offer that
 * no driver has, and `unassigned` with the calls no driver has, each only
 * to a member who may see them. Unassigned asks for the open statuses, so
 * an outside offer, which is OFFERED, is listed once, under its own.
 */
export function callSectionParams(section: CallSection) {
  switch (section) {
    case 'offers':
      return {
        outsideOffers: true,
        sortBy: 'board' satisfies CallSortBy,
      };
    case 'unassigned':
      return {
        unassigned: true,
        status: OPEN_STATUSES,
        sortBy: 'board' satisfies CallSortBy,
      };
  }
}

/**
 * The GET /calls query for the assigned-call prompt: the member's own calls
 * the office has assigned and nobody has started, oldest first, so the one
 * waiting longest is asked about first. Not ACCEPTED_AWAITING: that is an
 * outside call the driver took themselves, with nothing to start until the
 * motor club confirms it.
 */
export function newAssignmentParams() {
  return {
    assignedToMe: true,
    status: ['NEW', 'PENDING'],
    sortBy: 'date-asc' satisfies CallSortBy,
  };
}
